import { createContext, useContext, useEffect, useState } from 'react';

import { useTags, useTeams } from 'Hooks';
import { getECommerceDemo, getInteralDriverDemo, getInternalDriverDemoBackgroundTasks } from 'Services';
import { DemoSettings } from 'Types';
import { TagsLoadingError, TeamsLoadingError } from '../errors/DemoListErrors';

export type DemoListProviderProps = {
  demoList: { data: DemoSettings[]; isLoading: boolean };
  backgroundTasksList: DemoSettings[];
  updateDemoList: (demoIndex: number, update: Record<string, any>) => void;
  errors: Error[] | null;
};
export const DemoListContext = createContext({ demoList: [] } as unknown as DemoListProviderProps);

export function DemoListProvider({ children }: any) {
  const [demoList, setDemoList] = useState<DemoSettings[]>([]);
  const [backgroundTasksList, setBackgroundTasksList] = useState<DemoSettings[]>([]);
  const [errors, setErrors] = useState<Error[] | null>(null);
  const { teams, isLoadingTeams } = useTeams();
  const { tags, isLoadingTags } = useTags();

  const updateDemoList = (demoIndex: number, update: Record<string, any>) => {
    const remapFields = (field: any) => {
      const value =
        field.component === 'select'
          ? field.options.find((option: any) => option.id === update[field.name])
          : update[field.name];

      return {
        ...field,
        value: value,
      };
    };

    demoList[demoIndex].fields = demoList[demoIndex].fields.map(remapFields);
    backgroundTasksList[demoIndex].fields = backgroundTasksList[demoIndex].fields.map(remapFields);

    setDemoList(demoList);
    setBackgroundTasksList(backgroundTasksList);
  };

  useEffect(() => {
    if ((!teams.length && !isLoadingTeams) || (!tags.length && !isLoadingTags)) {
      const loadErrors: Error[] = [];
      if (!teams.length) {
        loadErrors.push(new TeamsLoadingError('Could not load teams'));
      }
      if (!tags.length) {
        loadErrors.push(new TagsLoadingError('Could not load tags'));
      }
      setDemoList([getECommerceDemo(teams, tags), getInteralDriverDemo(teams, tags)]);
      setErrors(loadErrors);

      return;
    }

    setErrors(null);
    setDemoList([getECommerceDemo(teams, tags), getInteralDriverDemo(teams, tags)]);
    setBackgroundTasksList([getECommerceDemo(teams, tags), getInternalDriverDemoBackgroundTasks(teams, tags)]);
  }, [teams, tags, isLoadingTeams, isLoadingTags]);

  return (
    <DemoListContext.Provider
      value={{
        demoList: { data: demoList, isLoading: isLoadingTeams || isLoadingTags },
        backgroundTasksList,
        updateDemoList,
        errors,
      }}
    >
      {children}
    </DemoListContext.Provider>
  );
}

export const useDemoList = () => useContext<DemoListProviderProps>(DemoListContext);
